import styled from '@emotion/styled';
import { Button } from 'antd';
import { isFunction } from 'lodash-es';
import { memo, ReactNode, useMemo } from 'react';
import { useTranslate } from '@tolgee/react';
import { LocalMessage } from 'hooks/business/conversation';
import { isTiKTok } from 'lib/utils/isTikTok';
import { useMemoizedFn } from 'ahooks';

const Line = styled.div`
  padding: 7px 14px;
  word-wrap: break-word;
  background-color: #fff7f7;
  color: #515359;
  padding-bottom: 8px;
  margin-top: 8px;
`;

export interface ErrorReasonProps {
  msg?: string;
  message: LocalMessage;
  className?: string;
  reSendImage?: Conversation.FailContent['reSendImage'];
  extra?: ReactNode;
}

const ErrorReason = (props: ErrorReasonProps) => {
  const { msg, message, className, reSendImage, extra } = props;
  const { msgId } = message;
  const { t } = useTranslate();

  const formatTikTokFailCause = useMemoizedFn((failCause: Conversation.FailContent) => {
    const { code } = failCause;
    console.log('code', code);
    if (code === 'TIMEOUT') {
      return t('scene.chatWorkspace.messageEditor.error.videoSendFailedRetry');
    } else {
      try {
        const rpaError = JSON.parse(code) as { code: number; cause?: string };
        const rpaErrorCode = rpaError?.code;
        const rpaErrorCause = rpaError?.cause;
        if (rpaErrorCode === 1) {
          return t('scene.chatWorkspace.messageEditor.error.videoSendFailedCheckPlugin');
        }
        if (rpaErrorCode === 2) {
          return t('scene.chatWorkspace.messageEditor.error.videoSendFailedCheckNetwork');
        }
        if (rpaErrorCode === 3) {
          return t('scene.chatWorkspace.messageEditor.error.videoSendFailedCheckNetwork');
        }
        if (rpaErrorCode === 4) {
          return t('scene.chatWorkspace.messageEditor.error.videoSendFailedCheckNetwork');
        }
        if (rpaErrorCode === 5) {
          return t('scene.chatWorkspace.messageEditor.error.videoSendFailedRetry');
        }
        if (rpaErrorCode === 6) {
          return t('scene.chatWorkspace.messageEditor.error.videoSendFailedCheckBuyerExists');
        }
        if (rpaErrorCode === 7) {
          return t('scene.chatWorkspace.messageEditor.error.videoSendFailedCheckNetwork');
        }
        if (rpaErrorCode === 8) {
          const r1 = 'is currently helping this user';
          if (rpaErrorCause?.includes(r1)) {
            const [customerService] = rpaErrorCause.split(r1);
            if (customerService.trim()) {
              return t('scene.chatWorkspace.messageEditor.error.chatServedByInSellerCenter', {
                x: customerService.trim(),
              });
            }
          }
          return t('scene.chatWorkspace.messageEditor.error.videoSendFailedCheckConversation');
        }
        if (rpaErrorCode === 9) {
          return t('scene.chatWorkspace.messageEditor.error.videoSendFailedCheckConversation');
        }
        if (rpaErrorCode === 10) {
          return t('scene.chatWorkspace.messageEditor.error.sellerCenterSearchFailed');
        }
        console.log('rpaError', rpaError);
      } catch (error) {
        console.log('error', error);
      }
    }
    return null;
  });

  const msgText = useMemo(() => {
    const { channel, failCause } = message;
    if (isTiKTok(channel) && failCause) {
      const result = formatTikTokFailCause(failCause);
      if (result) {
        return result;
      }
    }

    return msg;
  }, [formatTikTokFailCause, message, msg]);

  if (!msgText) {
    return null;
  }

  return (
    <div>
      <Line style={{ display: 'inline-block' }} className={className}>
        {msgText}
        {isFunction(reSendImage) ? (
          <Button type="link" onClick={() => reSendImage(msgId)}>
            {t('common.action.retry')}
          </Button>
        ) : null}
      </Line>
      {extra}
    </div>
  );
};
export default memo(ErrorReason);
